/**
 * Image Card V2
 *
 * Einzelne Bildkarte in der Galerie mit:
 * - Dauerhaftes Auswahl-Checkbox (oben links)
 * - Hover-Aktionen:
 *   - Lupe (mittig, grün)
 *   - Variation + Download (oben rechts)
 *   - Veröffentlichen + Löschen (unten links)
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Layers, Download, Globe, Trash2, Check, AlertTriangle } from 'lucide-react';
import { useImageActions } from '@/hooks/useImageActions';
import { cn } from '@/lib/utils';
import type { Image } from '@/types';

export interface ImageCardProps {
  image: Image;
  /** Ob diese Karte das Featured Image ist (groß dargestellt) */
  isFeatured?: boolean;
  /** Callback wenn Bild angeklickt wird (Modal öffnen) */
  onViewDetails?: (image: Image) => void;
  /** Ob das Bild ausgewählt ist */
  isSelected?: boolean;
  /** Callback wenn Auswahl geändert wird */
  onSelectionChange?: (imageId: string, selected: boolean) => void;
}

export function ImageCard({
  image,
  isFeatured = false,
  onViewDetails,
  isSelected = false,
  onSelectionChange,
}: ImageCardProps) {
  const { t } = useTranslation();
  const [showActions, setShowActions] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showConfirmPublish, setShowConfirmPublish] = useState(false);

  const {
    deleteImage,
    publishImage,
    downloadImage,
    generateVariation,
    isDeleting,
    isPublishing,
    isDownloading,
    isGeneratingVariation,
  } = useImageActions();

  const imageUrl = `/thumb/${image.id}`;

  const handleDelete = () => {
    deleteImage(image.id);
    setShowConfirmDelete(false);
  };

  const handlePublish = () => {
    publishImage(image.id);
    setShowConfirmPublish(false);
  };

  const handleViewDetails = () => {
    onViewDetails?.(image);
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelectionChange?.(image.id, !isSelected);
  };

  return (
    <div
      className={cn(
        'group relative rounded-xl overflow-hidden',
        'bg-neutral-100 dark:bg-neutral-800',
        'transition-all duration-200',
        'hover:shadow-lg hover:shadow-black/10 dark:hover:shadow-black/30',
        isSelected && 'ring-4 ring-orange-500',
        isFeatured && 'col-span-2 row-span-2'
      )}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => {
        setShowActions(false);
        setShowConfirmDelete(false);
        setShowConfirmPublish(false);
      }}
    >
      {/* Image Container */}
      <div className="aspect-square relative">
        <img
          src={imageUrl}
          alt={image.prompt || 'Generiertes Bild'}
          loading="lazy"
          className="w-full h-full object-cover"
        />

        {/* Auswahl-Checkbox (oben links, IMMER sichtbar) */}
        <button
          role="checkbox"
          aria-checked={isSelected}
          aria-label={
            image.prompt
              ? t('gallery.selectImage', { name: image.prompt.slice(0, 50) + (image.prompt.length > 50 ? '...' : '') })
              : t('gallery.selectImageGeneric', { id: image.id })
          }
          className={cn(
            'absolute top-3 left-3 z-20',
            'w-7 h-7 rounded-md',
            'flex items-center justify-center',
            'border-2 transition-all duration-150',
            isSelected
              ? 'bg-orange-500 border-orange-500 text-white'
              : 'bg-white/90 border-neutral-300 hover:border-orange-400',
            'shadow-sm'
          )}
          onClick={handleCheckboxClick}
        >
          {isSelected && <Check className="h-4 w-4" strokeWidth={3} />}
        </button>

        {/* Public Badge (oben rechts, verschoben wenn Actions sichtbar) */}
        {image.is_public_image && !showActions && (
          <span className="absolute top-3 right-3 z-10 flex items-center gap-1 px-2 py-1 rounded-md bg-green-500 text-white text-xs font-medium shadow-sm">
            <Globe className="h-3 w-3" />
          </span>
        )}

        {/* Hover Overlay mit Aktionen */}
        {showActions && !showConfirmDelete && !showConfirmPublish && (
          <div className="absolute inset-0 z-10 bg-black/50 backdrop-blur-[2px] flex items-center justify-center">
            {/* Oben rechts: Variation + Download */}
            <div className="absolute top-3 right-3 flex gap-2">
              <button
                className={cn(
                  'p-2 rounded-full bg-white hover:bg-neutral-100',
                  'text-neutral-700 shadow-md',
                  'transition-colors duration-150',
                  'disabled:opacity-50'
                )}
                onClick={() => generateVariation(image.id)}
                disabled={isGeneratingVariation}
                title={t('toast.success.variationGenerating')}
              >
                {isGeneratingVariation ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-neutral-400 border-t-transparent" />
                ) : (
                  <Layers className="h-4 w-4" />
                )}
              </button>
              <button
                className={cn(
                  'p-2 rounded-full bg-white hover:bg-neutral-100',
                  'text-neutral-700 shadow-md',
                  'transition-colors duration-150',
                  'disabled:opacity-50'
                )}
                onClick={() => downloadImage(image.id)}
                disabled={isDownloading}
                title={t('common.download')}
              >
                {isDownloading ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-neutral-400 border-t-transparent" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
              </button>
            </div>

            {/* Mitte: Lupe (grün) */}
            <button
              className={cn(
                'p-4 rounded-full',
                'bg-emerald-500 hover:bg-emerald-600',
                'text-white shadow-lg',
                'transition-all duration-150',
                'hover:scale-105'
              )}
              onClick={handleViewDetails}
              title={t('gallery.enlarge')}
            >
              <Search className={cn('h-6 w-6', isFeatured && 'h-8 w-8')} />
            </button>

            {/* Unten links: Veröffentlichen + Löschen */}
            <div className="absolute bottom-3 left-3 flex gap-2">
              {!image.is_public_image && (
                <button
                  className={cn(
                    'p-2 rounded-full bg-green-500 hover:bg-green-600',
                    'text-white shadow-md',
                    'transition-colors duration-150',
                    'disabled:opacity-50'
                  )}
                  onClick={() => setShowConfirmPublish(true)}
                  disabled={isPublishing}
                  title={t('common.publish')}
                >
                  {isPublishing ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    <Globe className="h-4 w-4" />
                  )}
                </button>
              )}
              <button
                className={cn(
                  'p-2 rounded-full bg-red-500 hover:bg-red-600',
                  'text-white shadow-md',
                  'transition-colors duration-150',
                  'disabled:opacity-50'
                )}
                onClick={() => setShowConfirmDelete(true)}
                disabled={isDeleting}
                title={t('common.delete')}
              >
                {isDeleting ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>
        )}

        {/* Confirm Delete Overlay */}
        {showConfirmDelete && (
          <div className="absolute inset-0 z-20 bg-black/70 flex flex-col items-center justify-center gap-3 p-4">
            <p className="text-white text-sm font-medium">{t('gallery.deleteQuestion')}</p>
            <div className="flex gap-2">
              <button
                className={cn(
                  'px-4 py-2 rounded-lg text-sm font-medium',
                  'bg-red-500 hover:bg-red-600 text-white',
                  'disabled:opacity-50',
                  'transition-colors duration-150'
                )}
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : (
                  t('common.yes')
                )}
              </button>
              <button
                className={cn(
                  'px-4 py-2 rounded-lg text-sm font-medium',
                  'bg-neutral-600 hover:bg-neutral-700 text-white',
                  'transition-colors duration-150'
                )}
                onClick={() => setShowConfirmDelete(false)}
              >
                {t('common.no')}
              </button>
            </div>
          </div>
        )}

        {/* Confirm Publish Overlay */}
        {showConfirmPublish && (
          <div className="absolute inset-0 z-20 bg-black/70 flex flex-col items-center justify-center gap-3 p-4">
            <p className="text-white text-sm font-medium">{t('gallery.publishQuestion')}</p>
            <p className="text-amber-400 text-xs flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              {t('gallery.publishNotReversible')}
            </p>
            <div className="flex gap-2">
              <button
                className={cn(
                  'px-4 py-2 rounded-lg text-sm font-medium',
                  'bg-green-500 hover:bg-green-600 text-white',
                  'disabled:opacity-50',
                  'transition-colors duration-150'
                )}
                onClick={handlePublish}
                disabled={isPublishing}
              >
                {isPublishing ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : (
                  t('common.yes')
                )}
              </button>
              <button
                className={cn(
                  'px-4 py-2 rounded-lg text-sm font-medium',
                  'bg-neutral-600 hover:bg-neutral-700 text-white',
                  'transition-colors duration-150'
                )}
                onClick={() => setShowConfirmPublish(false)}
              >
                {t('common.no')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ImageCard;
